import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { useUpload } from 'contexts/UppyContext'
import { drawerWidth } from 'store/constant'
import Panel from 'components/design-system/Panel'
import UploadProgressItem from 'components/design-system/UploadProgressItem'

const STATUS_MAP = { uploading: 'uploading', completed: 'done', failed: 'failed' }
const AUTO_HIDE_MS = 5000

/** Upload progress panel (Figma 96:23060): Panel floating + danh sách Upload Progress Item. */
const ProgressUpload = React.memo(() => {
  const { uploadBatches = [], cancelUpload, clearFinishedUploads } = useUpload() || {}
  const [hidden, setHidden] = useState(false)
  const prevCount = useRef(uploadBatches.length)

  // Hiện lại panel khi có batch mới
  useEffect(() => {
    if (uploadBatches.length > prevCount.current) setHidden(false)
    prevCount.current = uploadBatches.length
  }, [uploadBatches.length])

  // Tự ẩn: khi mọi batch đã xong (completed / failed), đợi AUTO_HIDE_MS rồi xoá khỏi panel
  const allFinished = uploadBatches.length > 0 && uploadBatches.every((b) => b.status !== 'uploading')
  useEffect(() => {
    if (!allFinished) return undefined
    const timer = setTimeout(() => clearFinishedUploads?.(), AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [allFinished, uploadBatches.length, clearFinishedUploads])

  // Đóng: xoá batch đã xong và ẩn panel (upload đang chạy vẫn tiếp tục)
  const handleClose = () => {
    clearFinishedUploads?.()
    setHidden(true)
  }

  if (hidden || uploadBatches.length === 0) return null

  const done = uploadBatches.filter((b) => b.status === 'completed').length
  const uploading = uploadBatches.some((b) => b.status === 'uploading')

  return (
    <Panel
      elevation="floating"
      icon="layers"
      title="Upload progress"
      onClose={handleClose}
      sx={{ position: 'fixed', left: drawerWidth + 16, bottom: 16, zIndex: 1250, width: 296 }}
      bodySx={{ flex: '0 1 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-12)', px: 'var(--space-16)' }}
    >
      <Box
        sx={{
          fontFamily: 'var(--font-family-primary)',
          fontWeight: 'var(--font-weight-regular)',
          fontSize: 'var(--font-size-12)', // Caption/12 Regular
          lineHeight: 'normal',
          color: 'var(--color-text-secondary)'
        }}
      >
        {done}/{uploadBatches.length} {uploading ? 'uploading' : 'uploaded'}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)', maxHeight: 228, overflowY: 'auto' }}>
        {uploadBatches.map((batch) => (
          <UploadProgressItem
            key={batch.id}
            name={batch.name}
            status={STATUS_MAP[batch.status] || 'uploading'}
            progress={batch.progress}
            onCancel={cancelUpload ? () => cancelUpload(batch.id) : undefined}
          />
        ))}
      </Box>
    </Panel>
  )
})

ProgressUpload.displayName = 'ProgressUpload'

export default ProgressUpload
